import { AccountPlanName, UserRole } from '@adjust/dashboard-core/api/types';

// Schemas
export interface Feature {
  [key: string]: { enabled: boolean };
}

export enum Currency {
  EUR = 'EUR',
  USD = 'USD',
  JPY = 'JPY',
}

export interface CurrencyInfo {
  symbol: string;
  name: string;
  fraction: number;
}

export interface Plan {
  id: number;
  name: AccountPlanName;
  yearly: boolean;
  nextBillingDate: string;
  attributionPrice: number;
  currency: Currency;
  basePrice: number;
  includedAttributions: number;
  flat: boolean;
  current: boolean;
  features: Feature[];
  scheduled: boolean;
  activeSince: string;
}

export enum DistributionFilterOption {
  Standard = 'standard',
  Advanced = 'advanced',
}

export interface FraudPreventionSettings {
  showInvalidSignature?: boolean;
  filterAnonymousTraffic?: boolean;
  filterTooManyEngagements?: boolean;
  filterEngagementInjection?: boolean;
  distributionFilter?: DistributionFilterOption;
}

export interface SsoSettings {
  sso_target: string;
  idp_identity_id: string;
  certificate: string;
}

export interface GoogleSsoConfig {
  domains: string[];
  settings: SsoSettings;
}

export interface PaymentMethodData {
  token: string;
  default: boolean;
  cardType: string;
  maskedNumber: string;
  expiration: string;
}

export interface S2sToken {
  id: number;
  per_app_index: number;
  label: string;
  token: string;
  active: boolean;
}

export interface Platform {
  configured: boolean;
  app_id: string;
}

export interface Platforms {
  [key: string]: Platform;
}

export interface AppPermissions {
  update_settings: boolean;
  update_custom_twitter_permissions: boolean;
}

export interface App {
  id: number;
  name: string;
  token: string;
  default_store_app_id: string;
  default_platform: string;
  default_attribution_platform: string;
  platforms: Platforms;
  permissions: AppPermissions;
}

/* eslint-disable @typescript-eslint/naming-convention */
export interface Device {
  Adid: string;
  AdvertisingId: string;
  [otherProps: string]: any;
}
/* eslint-enable @typescript-eslint/naming-convention */

export interface DevicePayload {
  id: string;
  appToken: string;
}

export interface TwoFactorConfig {
  enabled: boolean;
}

export interface AppEventUrls {
  archive?: string;
  unarchive?: string;
}

export interface AppEvent {
  id: number;
  name: string;
  token: string;
  callback_url: Nullable<string>;
  unique: boolean;
  autogenerated: boolean;
  archived: boolean;
  urls: AppEventUrls;
}

// responses

export type SKAdNetworkSettingsEventsMapping = {
  [key: string]: Nullable<string>;
};

export type SKAdNetworkSettingsResponse = {
  enabled: boolean;
  events_mapping: SKAdNetworkSettingsEventsMapping;
  cut_off_period: number | null;
};

export interface EmailReportsMailingValue {
  cohorts: boolean;
  deliverables: boolean;
  type_7days: boolean;
  type_30days: boolean;
}

export type EmailReportsMailingsKey = 'daily' | 'weekly' | 'monthly';

export type EmailReportsMailing = {
  [key in EmailReportsMailingsKey]?: EmailReportsMailingValue;
};

export interface EmailReportsSettings {
  to: string;
  mailings: EmailReportsMailing;
}

export interface HistoryEventAttribute<T> {
  action: string;
  actor_gid: string;
  actor_name: string;
  category: Nullable<string>;
  internal: boolean;
  kind: string;
  name: string;
  occurred_at: Date;
  parameters: T;
}

export type HistoryEventParametersLexi = Partial<{
  groupName: string;
  audienceName: string;
  exportType: string;
  newAccountName: string;
  oldAccountName: string;
  fullNameOrEmail: string;
  secretVersion: string;
  appName: string;
  termName: string;
  partnerName: string;
  credentialName: string;
}>;

export type HistoryEventSegment = { name: string; groups: { uuid: string; metadata: { name: string } }[] };

export type HistoryEventParameters = HistoryEventParametersLexi &
  Partial<{
    segment: HistoryEventSegment;
    group_uuid: string;
    export_type: string;
    new_account_name: string;
    old_account_name: string;
    full_name: string;
    email: string;
    secret_version: string;
    mobile_app_name: string;
    term_name: string;
    partner: string;
    partner_display_name: string;
    credential_name: string;
    store: string;
    label: string;
    account_id: number;
    index: number;
    mobile_app: { default_store_app_id: string };
    default_store_app_id: string;
    store_app_ids: string[];
  }>;

export interface HistoryEvent<T = HistoryEventParameters> {
  id: string;
  attributes: HistoryEventAttribute<T>;
}

// Responses

export type GetAuditEventsResponse = { data: HistoryEvent[]; links: { first: string; last: string; next; string } };

export enum PartnerCategory {
  facebook = 'facebook',
  analytics = 'analytics',
  network = 'network',
}

export enum PartnerAppSettingsFieldType {
  boolean = 'boolean',
  string = 'string',
  select = 'select',
  array = 'array',
  customSelect = 'custom_select',
}

export interface PartnerAppSettingsField {
  key: string;
  type: PartnerAppSettingsFieldType;
  value: unknown;
  label?: string;
  async?: boolean;
  options?: string[];
  optionsLabel?: Record<string, string>;
  optional?: boolean;
  validation?: string;
}

export interface PartnerAppSettingsConversion {
  conversionId: string | null;
  settings: PartnerAppSettingsField[];
}

export interface PartnerAppSettings {
  integrated: boolean;
  revenueForwarding: boolean;
  parameterForwarding: boolean;
  sessionForwarding: boolean;
  costForwarding: boolean;
  attributedOnly: boolean;
  uninstallForwarding: boolean;
  adRevenueForwarding: boolean;
  inactiveUserDefinition: boolean;
  settings: (PartnerAppSettingsField | PartnerAppSettingsConversion)[];
  limitedDataUse?: boolean;
  attributionWindow?: boolean;
}

export type PartnerEventTypeSettingsKey = PartnerAppPlatform | 'settings';

export type PartnerEventTypeSettings = {
  eventTypeId: number;
  eventTypeToken: string;
  eventTypeName: string;
} & Partial<Record<PartnerEventTypeSettingsKey, PartnerAppSettingsField[]>>;

export interface Partner {
  id: number;
  name: PartnerName;
  category: PartnerCategory | null;
  activitiesToForward: string[] | null;
  partnerAppSettings: Partial<Record<PartnerAppPlatform, PartnerAppSettings>> | PartnerAppSettings;
  partnerDisplayName: string | null;
  partnerEventTypeSettings: PartnerEventTypeSettings[];
  partnerIntegrated: boolean;
  pmdKeyword: string | null;
  supportsAttributedOnly: boolean;
  supportsAttributionWindow: boolean;
  supportsConversionIds: boolean;
  supportsCostForwarding: boolean;
  supportsEventLinking: boolean;
  supportsInactiveUserDefinition: boolean;
  supportsParameterForwarding: boolean;
  supportsRevenueForwarding: boolean;
  supportsAdRevenueForwarding: boolean;
  supportsSessionForwarding: boolean;
  supportsUninstallForwarding: boolean;
  switchActivation: boolean;
  wasIntegratedBefore: boolean | null;
  selected?: boolean;
}

export interface PartnerFacebook extends Partner {
  acceptedTc: boolean;
}

export interface PartnerTwitter extends Omit<Partner, 'partnerAppSettings'> {
  twitterAdsAccountsEnabled: boolean;
  partnerAppSettings: PartnerAppSettings & {
    twitterCustomPermissions: boolean;
  };
}

export enum TwitterErrorCode {
  multipleProviders = 'multiple_providers',
  unknown = 'unknown',
}

export interface TwitterError {
  code: TwitterErrorCode;
  accountName: string;
}

export enum SpecialPartner {
  facebook = 34,
  twitter = 32,
}

export enum PartnerName {
  facebook = 'facebook',
  twitter = 'twitter',
  tencent = 'tencent',
  tapjoy = 'tapjoy',
  sponsorpay = 'sponsorpay',
  adparlor = 'adparlor',
  experian = 'experian',
  adquant = 'adquant',
  google = 'google',
}

export interface SubscriptionCredentials {
  appleSharedSecret: Nullable<string>;
  googleCredentialsFilename: Nullable<string>;
  googleCredentialsFile: Nullable<File>;
}

export interface SubAppCallback {
  id: string;
  name: string;
  url: Nullable<string>;
}

export interface AppCallback {
  custom: boolean;
  id: string;
  name: string;
  url: Nullable<string>;
  subCallbacks: SubAppCallback[];
}

export enum PartnerAppPlatform {
  ios = 'ios',
  android = 'android',
  windows = 'windows',
  'windows-phone' = 'windows-phone',
}

export enum InvoiceType {
  Regular = 'regular',
  PrePayment = 'pre_payment',
  CreditNote = 'credit_note',
}

export interface Invoice {
  number: string;
  invoiceType: InvoiceType;
  status: string;
  billingPeriodStart: string;
  billingPeriodEnd: string;
  totalPriceNet: string;
  downloadUrl: string;
  dueAt?: string;
  totalAttributions?: number;
}

export interface CurrentCharge {
  begin: string;
  end: string;
  totalAttributions: number;
  totalPriceNet: string;
}

export type TencentAdAccount = {
  id: number;
  active: boolean;
  renewTokenExpiresSoon: boolean;
  tencentAccountId: string;
  tencentAccountUin: string;
  connectedMobileApps: { name: string; token: string }[];
};

export type Tracker = {
  token: string;
  label: string;
  selected?: boolean;
};

export type DisplayRole = Exclude<typeof UserRole[keyof typeof UserRole], 'super-admin'>;
export type DisplayRolesCount = Record<DisplayRole, number>;

export type AdPartner = 'twitter' | 'facebook' | 'google';
export type PartnerAdAccountsCount = Record<AdPartner, number>;
